import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
// src/features/seec/sections/UnifiedExpenditureSections.jsx

import React, { useEffect, useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { CommonTable } from "../../../shared/ui/table/CommonTable";
import { HoverPopover } from "../../../shared/ui/overlay/HoverPopover.jsx";
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import { toJSDate, formatMMDDYYYY } from "../utils/componentsUtils";
import { loadSeecDocuments } from "../utils/loadSeecDocuments";

function UnifiedExpenditureSection({
  collapsed,
  toggle,
  title = "All Expenditures",
  docIds = ["GDTC-Expenditures"],
  rows = null,
  color = "#fee2e2",
}) {
  const [allData, setAllData] = useState([]);
  const [data, setData] = useState([]);
  const [aggregated, setAggregated] = useState([]);
  const [showTable, setShowTable] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:showTable:" + [title].join(":"), false);
  const [showTop, setShowTop] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:showTop:" + [title].join(":"), false);
  const [showChart, setShowChart] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:showChart:" + [title].join(":"), false);
  const [useSummary, setUseSummary] = useState([]);
  const [showUses, setShowUses] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:showUses:" + [title].join(":"), false);
  const [showUseChart, setShowUseChart] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:showUseChart:" + [title].join(":"), false);
  const [topYearWindow, setTopYearWindow] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:topYearWindow:" + [title].join(":"), "5");
  const [rawYearWindow, setRawYearWindow] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:rawYearWindow:" + [title].join(":"), "5");
  const [useYearWindow, setUseYearWindow] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:useYearWindow:" + [title].join(":"), "5");
  const [usePieYearWindow, setUsePieYearWindow] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:usePieYearWindow:" + [title].join(":"), "5");
  const [chartYearWindow, setChartYearWindow] = useSharedFilter("UnifiedExpenditureSection.jsx:UnifiedExpenditureSection:chartYearWindow:" + [title].join(":"), "5");

  const yearWindowOptions = ["ALL", "1", "3", "5", "10", "15"];

  const round = (num) => Math.round(num * 100) / 100;

  function extractHexFromTailwind(bgClass = "") {
    const match = bgClass.match(/#([0-9a-fA-F]{6})/);
    const hex = match ? match[1] : "f87171"; // fallback to red
    const amt = -20;
    const darken = (component) =>
      Math.max(0, parseInt(component, 16) + amt)
        .toString(16)
        .padStart(2, "0");
    return `#${darken(hex.slice(0, 2))}${darken(hex.slice(2, 4))}${darken(
      hex.slice(4, 6),
    )}`;
  }

  function filterRowsByYearWindow(entries, windowValue) {
    const validYears = entries
      .map((entry) => entry.__parsedDate)
      .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
      .map((date) => date.getFullYear())
      .filter((year) => year > 1970);
    const latestYear = validYears.length ? Math.max(...validYears) : null;
    const minYear =
      windowValue === "ALL" || latestYear === null
        ? null
        : latestYear - Number(windowValue) + 1;

    return minYear === null
      ? entries
      : entries.filter((entry) => entry.__parsedDate.getFullYear() >= minYear);
  }

  function buildExpenditurePopoverRows(raw) {
    if (!raw || typeof raw !== "object") return [];

    const address = [raw.streetAddress, raw.city, raw.state]
      .filter(Boolean)
      .join(", ");

    const orderedFields = [
      ["purposeOfExpenditure", "Purpose of Expenditure"],
      ["description", "Description"],
      ["address", "Address"],
      ["methodOfPayment", "Method of Payment"],
      ["amountOriginal", "Amount"],
      ["dateOfPayment", "Date of Payment"],
      ["fileToState", "Filed to State"],
      ["periodStart", "Period Start"],
      ["periodEnd", "Period End"],
      ["status", "Status"],
      ["dataSource", "Data Source"],
      ["refiledElectronically", "Refiled Electronically"],
      ["committeePartyAffiliation", "Committee Party Affiliation"],
      ["disbursementId", "Disbursement ID"],
    ];

    const source = {
      ...raw,
      address,
    };

    return orderedFields
      .map(([key, label]) => {
        const value = source[key];
        if (value == null) return null;
        if (typeof value === "string" && value.trim() === "") return null;
        return { key, label, value: String(value) };
      })
      .filter(Boolean);
  }

  function buildUseSummaryRows(entries, windowValue) {
    const useMap = {};

    for (const row of filterRowsByYearWindow(entries, windowValue)) {
      const use = row["Use"]?.trim() || "Unspecified";
      const amount = row["Amount (Sort)"];

      if (!useMap[use]) {
        useMap[use] = {
          use,
          total: 0,
          count: 0,
          max: amount,
          min: amount,
          payments: [],
        };
      }

      useMap[use].total += amount;
      useMap[use].count += 1;
      useMap[use].max = Math.max(useMap[use].max, amount);
      useMap[use].min = Math.min(useMap[use].min, amount);
      useMap[use].payments.push({
        date: row.__dateDisplay ?? "—",
        sortDate:
          row.__parsedDate instanceof Date &&
          !Number.isNaN(row.__parsedDate.getTime())
            ? row.__parsedDate.getTime()
            : 0,
        amount: Math.round(amount || 0),
        description: row["Detailed Purpose"]?.trim() || "—",
      });
    }

    return Object.values(useMap)
      .map((entry) => {
        const total = Math.round(entry.total);
        const average = entry.count ? Math.round(entry.total / entry.count) : 0;
        const max = Math.round(entry.max);
        const min = Math.round(entry.min);
        const payments = [...entry.payments].sort(
          (a, b) => b.sortDate - a.sortDate,
        );
        return {
          Use: entry.use,
          "Total Spent": `$${total.toLocaleString()}`,
          "Total Spent (Sort)": total,
          "Average Spent": average,
          "Average Spent (Sort)": average,
          "Max Spent": max,
          "Max Spent (Sort)": max,
          "Min Spent": min,
          "Min Spent (Sort)": min,
          "Number of Payments": entry.count,
          paymentHistory: payments,
          rawTotal: total,
        };
      })
      .sort((a, b) => b.rawTotal - a.rawTotal);
  }

  useEffect(() => {
    async function fetchData() {
      const fetchedRows = rows
        ? rows
        : (await loadSeecDocuments(docIds)).flatMap(
            (loadedDoc) => loadedDoc.data || [],
          );

      const cleaned = fetchedRows
        .map((entry) => {
          const amountStr = entry["Amount"];
          const amountNum =
            typeof amountStr === "number"
              ? amountStr
              : parseFloat(amountStr?.replace(/[^\d.-]/g, "")) || 0;

          const parsedDate = toJSDate(entry["Date"]);

          return {
            ...entry,
            "Amount (Sort)": amountNum,
            __parsedDate: parsedDate ?? new Date(0),
            __dateDisplay: formatMMDDYYYY(parsedDate),
          };
        })
        .sort((a, b) => b.__parsedDate - a.__parsedDate);

      setAllData(cleaned);
      setData(filterRowsByYearWindow(cleaned, rawYearWindow));

      // Aggregate by Payee
      const aggMap = {};
      for (const row of filterRowsByYearWindow(cleaned, topYearWindow)) {
        const vendor = row["Vendor / Recipient"]?.trim();
        const address = [row.streetAddress, row.city, row.state]
          .filter(Boolean)
          .join(", ");
        const amount = row["Amount (Sort)"];
        const key = vendor?.toLowerCase();

        // Skip empty vendor keys so we don't collapse into "undefined"
        if (!key) continue;

        if (!aggMap[key]) {
          aggMap[key] = {
            vendor,
            address,
            total: 0,
            count: 0,
            max: amount,
            min: amount,
            payments: [],
          };
        }

        if (address && !aggMap[key].address) {
          aggMap[key].address = address;
        }

        aggMap[key].total += amount;
        aggMap[key].count += 1;
        aggMap[key].max = Math.max(aggMap[key].max, amount);
        aggMap[key].min = Math.min(aggMap[key].min, amount);
        aggMap[key].payments.push({
          date: row.__dateDisplay ?? "—",
          sortDate:
            row.__parsedDate instanceof Date &&
            !Number.isNaN(row.__parsedDate.getTime())
              ? row.__parsedDate.getTime()
              : 0,
          description:
            row["Detailed Purpose"]?.trim() || row.Use?.trim() || "—",
          amount: Math.round(amount || 0),
        });
      }

      setUseSummary(buildUseSummaryRows(cleaned, useYearWindow));

      const result = Object.values(aggMap)
        .map((entry) => {
          const total = Math.round(entry.total);
          const average = entry.count
            ? Math.round(entry.total / entry.count)
            : 0;
          const max = Math.round(entry.max);
          const min = Math.round(entry.min);
          const payments = [...entry.payments].sort(
            (a, b) => b.sortDate - a.sortDate,
          );
          return {
            Vendor: entry.vendor,
            Address: entry.address || "—",
            "Total Spent": `$${total.toLocaleString()}`,
            "Total Spent (Sort)": total,
            "Average Spent": average,
            "Average Spent (Sort)": average,
            "Max Spent": max,
            "Max Spent (Sort)": max,
            "Min Spent": min,
            "Min Spent (Sort)": min,
            "Number of Payments": entry.count,
            paymentHistory: payments,
            rawTotal: total,
          };
        })
        .sort((a, b) => b.rawTotal - a.rawTotal);

      setAggregated(result);
    }

    fetchData();
  }, [docIds, rows, rawYearWindow, topYearWindow, useYearWindow]);

  const rawColumns = [
    {
      key: "Vendor / Recipient",
      label: "Payee",
      style: { width: "180px" },
      render: (row) => {
        const raw = row.__rawExpenditure;
        const detailRows = buildExpenditurePopoverRows(raw);
        const content = (
          <div className="p-3 text-xs text-gray-800">
            <div className="font-semibold text-sm text-gray-900 mb-2">
              {row["Vendor / Recipient"]}
            </div>
            {!detailRows.length ? (
              <div className="text-gray-500 italic">No details found.</div>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {detailRows.map((item) => (
                    <tr
                      key={`${row["Vendor / Recipient"]}-${item.key}`}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-2 py-1 align-top font-semibold text-gray-600 whitespace-nowrap">
                        {item.label}
                      </td>
                      <td className="px-2 py-1 align-top text-gray-900 break-words">
                        {item.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

        return (
          <div className="relative flex items-center min-h-[22px] pr-6">
            <span className="leading-tight">{row["Vendor / Recipient"]}</span>
            {detailRows.length > 0 && (
              <div className="absolute right-1 top-1/2 -translate-y-1/2">
                <HoverPopover
                  content={content}
                  popoverClassName="text-gray-800"
                  maxWidth={720}
                >
                  <InformationCircleIcon
                    className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
                    aria-label={`Show details for ${row["Vendor / Recipient"]}`}
                  />
                </HoverPopover>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "Date",
      label: "Date",
      style: { width: "70px" },
      align: "center",
      sortKey: "__parsedDate",
      render: (row) => row.__dateDisplay ?? "—",
    },
    {
      key: "Amount",
      label: "Amount",
      style: { width: "60px" },
      sortKey: "Amount (Sort)",
      format: "currency",
      align: "center",
      render: (row) => {
        const val = row["Amount"];
        const num =
          typeof val === "number"
            ? val
            : parseFloat(val?.replace(/[^\d.-]/g, "")) || 0;
        return `$${num.toLocaleString()}`;
      },
    },
    {
      key: "Purpose of Expenditure",
      label: "Purpose",
      style: { width: "230px" },
    },
    { key: "Detailed Purpose", label: "Details", style: { width: "230px" } },
  ];

  const topColumns = [
    {
      key: "Vendor",
      label: "Payee",
      style: { width: "220px" },
      render: (row) => {
        const payments = Array.isArray(row.paymentHistory) ? row.paymentHistory : [];
        const content = (
          <div className="p-3 text-xs">
            <div className="font-semibold text-sm text-gray-900 mb-2">
              {row.Vendor}
            </div>
            {!payments.length ? (
              <div className="text-gray-500 italic">No payments found.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="text-left font-semibold text-gray-600 px-2 py-1">
                      Date
                    </th>
                    <th className="text-left font-semibold text-gray-600 px-2 py-1">
                      Description
                    </th>
                    <th className="text-right font-semibold text-gray-600 px-2 py-1">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr key={`${row.Vendor}-${payment.sortDate}-${index}`} className="border-b border-gray-100 last:border-b-0">
                      <td className="px-2 py-1 align-top whitespace-nowrap">
                        {payment.date}
                      </td>
                      <td className="px-2 py-1 align-top">
                        {payment.description}
                      </td>
                      <td className="px-2 py-1 align-top text-right whitespace-nowrap">
                        ${payment.amount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

        return (
          <div className="relative flex items-center min-h-[22px] pr-6">
            <span className="leading-tight">{row.Vendor}</span>
            {payments.length > 0 && (
              <div className="absolute right-1 top-1/2 -translate-y-1/2">
                <HoverPopover
                  content={content}
                  popoverClassName="text-gray-800"
                  maxWidth={720}
                >
                  <InformationCircleIcon
                    className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
                    aria-label={`Show payments for ${row.Vendor}`}
                  />
                </HoverPopover>
              </div>
            )}
          </div>
        );
      },
    },
    { key: "Address", label: "Address", style: { width: "260px" } },
    {
      key: "Total Spent",
      label: "Total Spent",
      sortKey: "Total Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "100px" },
    },
    {
      key: "Average Spent",
      label: "Average Spent",
      sortKey: "Average Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "100px" },
    },
    {
      key: "Max Spent",
      label: "Max Spent",
      sortKey: "Max Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "100px" },
    },
    {
      key: "Min Spent",
      label: "Min Spent",
      sortKey: "Min Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "100px" },
    },
    {
      key: "Number of Payments",
      label: "# of Payments",
      style: { width: "100px" },
      align: "center",
    },
  ];

  const useColumns = [
    {
      key: "Use",
      label: "Use Category",
      style: { width: "400px" },
      render: (row) => {
        const payments = Array.isArray(row.paymentHistory) ? row.paymentHistory : [];
        const content = (
          <div className="p-3 text-xs text-gray-800">
            <div className="font-semibold text-sm text-gray-900 mb-2">
              {row.Use}
            </div>
            {!payments.length ? (
              <div className="text-gray-500 italic">No payments found.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="text-left font-semibold text-gray-600 px-2 py-1">
                      Date
                    </th>
                    <th className="text-left font-semibold text-gray-600 px-2 py-1">
                      Description
                    </th>
                    <th className="text-right font-semibold text-gray-600 px-2 py-1">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr
                      key={`${row.Use}-${payment.sortDate}-${index}`}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-2 py-1 align-top whitespace-nowrap">
                        {payment.date}
                      </td>
                      <td className="px-2 py-1 align-top">
                        {payment.description}
                      </td>
                      <td className="px-2 py-1 align-top text-right whitespace-nowrap">
                        ${payment.amount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );

        return (
          <div className="relative flex items-center min-h-[22px] pr-6">
            <span className="leading-tight">{row.Use}</span>
            {payments.length > 0 && (
              <div className="absolute right-1 top-1/2 -translate-y-1/2">
                <HoverPopover
                  content={content}
                  popoverClassName="text-gray-800"
                  maxWidth={720}
                >
                  <InformationCircleIcon
                    className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
                    aria-label={`Show payments for ${row.Use}`}
                  />
                </HoverPopover>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "Total Spent",
      label: "Total Spent",
      sortKey: "Total Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "90px" },
    },
    {
      key: "Average Spent",
      label: "Average Spent",
      sortKey: "Average Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "90px" },
    },
    {
      key: "Max Spent",
      label: "Max Spent",
      sortKey: "Max Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "90px" },
    },
    {
      key: "Min Spent",
      label: "Min Spent",
      sortKey: "Min Spent (Sort)",
      format: "currency",
      align: "center",
      style: { width: "90px" },
    },
    {
      key: "Number of Payments",
      label: "# of Payments",
      align: "center",
      style: { width: "90px" },
    },
  ];

  const usePieSummary = buildUseSummaryRows(allData, usePieYearWindow);

  // Pie chart data (top 5 + Other) derived from the chart year window
  const pieData = (() => {
    const top = usePieSummary.slice(0, 5);
    const other = usePieSummary
      .slice(5)
      .reduce((sum, u) => sum + (u.rawTotal || 0), 0);

    const base = top.map((u) => ({ name: u.Use, value: u.rawTotal || 0 }));
    if (other > 0) base.push({ name: "Other", value: other });
    return base;
  })();
  const usePieItems = pieData.map((entry) => ({
    key: entry.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    label: entry.name,
    value: entry.value,
    valueType: "currency",
  }));

  const chartAggregated = (() => {
    const aggMap = {};

    for (const row of filterRowsByYearWindow(allData, chartYearWindow)) {
      const vendor = row["Vendor / Recipient"]?.trim();
      const amount = row["Amount (Sort)"];
      const key = vendor?.toLowerCase();

      if (!key) continue;

      if (!aggMap[key]) {
        aggMap[key] = {
          vendor,
          total: 0,
        };
      }

      aggMap[key].total += amount;
    }

    return Object.values(aggMap)
      .map((entry) => ({
        Vendor: entry.vendor,
        rawTotal: Math.round(entry.total),
      }))
      .sort((a, b) => b.rawTotal - a.rawTotal);
  })();

  const topPayeesChartSeries = [
    {
      label: "Total Spent",
      data: chartAggregated.slice(0, 25).map((entry) => entry.rawTotal || 0),
      color: extractHexFromTailwind(color),
      fill: true,
    },
  ];

  return (
    <div className="mb-4 border border-gray-300 rounded">
      <button type="button" aria-expanded={!collapsed}
        className="w-full bg-gray-100 px-3 py-3 font-semibold text-lg text-gray-900 flex justify-between items-center text-left hover:bg-gray-200 rounded-none"
        onClick={toggle}>
        {title}
        <span aria-hidden="true">{collapsed ? "▸" : "▾"}</span>
      </button>

      {!collapsed && (
        <div className="bg-white p-4">
          {/* Aggregated Table */}
          <div className="text-md font-semibold mb-2 flex justify-between items-center gap-4 flex-wrap">
            <button type="button" aria-expanded={showTop}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowTop((prev) => !prev)}
              >
              <span>Top Payees</span>
              <span aria-hidden="true">{showTop ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">
                Number of Years
              </span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={topYearWindow}
                onChange={(e) => setTopYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showTop && (
            <CommonTable
              title=""
              columns={topColumns}
              data={aggregated}
              headerColor={color}
              showAggregateTotal={false}
            />
          )}

          {/* Raw Table */}
          <div className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap">
            <button type="button" aria-expanded={showTable}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowTable((prev) => !prev)}
              >
              <span>All Expenditures</span>
              <span aria-hidden="true">{showTable ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">
                Number of Years
              </span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={rawYearWindow}
                onChange={(e) => setRawYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showTable && (
            <CommonTable
              title=""
              columns={rawColumns}
              data={data}
              headerColor={color}
              showAggregateTotal={false}
            />
          )}

          <div className="my-6 border-t border-gray-300" />

          {/* Uses Table */}
          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showUses}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowUses((prev) => !prev)}
              >
              <span>Purpose of Expenditure</span>
              <span aria-hidden="true">{showUses ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={useYearWindow}
                onChange={(e) => setUseYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showUses && (
            <CommonTable
              title=""
              columns={useColumns}
              data={useSummary}
              headerColor={color}
              showAggregateTotal={false}
              defaultSortField="Total Spent"
              defaultSortOrder="desc"
            />
          )}

          {/* Uses Pie Chart */}
          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showUseChart}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowUseChart((prev) => !prev)}
              >
              <span>Expenditures by Use (Pie Chart)</span>
              <span aria-hidden="true">{showUseChart ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={usePieYearWindow}
                onChange={(e) => setUsePieYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showUseChart && (
            <div className="max-w-[900px]">
              <PieChartWithToggles
                title={null}
                items={usePieItems}
                percentOf="all"
                showLegend={false}
                showSeriesToggles={false}
                showChartTypeSelector={false}
                aggregateOthers={false}
                includeZeroValues={true}
                showLegendTable={true}
                legendTablePlacement="right"
                legendTableMaxHeight="360px"
                enableHoverPop={true}
                hoverPopOffset={12}
                sliceOrder="valueDesc"
              />
            </div>
          )}

          {/* Bar Chart */}
          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showChart}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowChart((prev) => !prev)}
              >
              <span>Top 25 Vendor Payments (Bar Graph)</span>
              <span aria-hidden="true">{showChart ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={chartYearWindow}
                onChange={(e) => setChartYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showChart && (
            <LineChartWithToggles
              labels={chartAggregated.slice(0, 25).map((entry) => entry.Vendor)}
              series={topPayeesChartSeries}
              title={null}
              showTotalWhenNoneSelected={false}
              showSeriesToggles={false}
              showLegend={false}
              showChartTypeSelector={true}
              initialChartType="bar"
              defaultYAxisMode="zero"
              yAxisTitle="Total Spent"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default UnifiedExpenditureSection;
